import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Award, Lock, Play, ShieldCheck, Trophy, UserPlus, Users, Clock, TriangleAlert, ChevronRight } from "lucide-react";
import { PortalPage, Card, Btn, Spinner, ErrorNote, TextLink } from "@/components/PortalPage";
import { ModuleBadge } from "@/components/LsoParts";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { lsoTrack, nextModule, canTakeFinal, passedCount, qs } from "@/lib/lso";

const StatusBanner = ({ status }) => {
  if (!status) return null;
  const s = status.status;
  const lock = s === "locked" || s === "renewal_overdue";
  let title = null, body = null;
  if (s === "past_due") { title = `LSO Training PAST DUE — ${Math.abs(status.days_remaining || 0)} day(s) overdue`; body = `You have ${Math.max(0, 45 - (status.days_since_start || 0))} day(s) before your account is locked. Complete LSO Training now.`; }
  if (s === "locked") { title = status.self_unlock ? "Account LOCKED — finish this course to unlock" : "Account LOCKED — admin reset required"; body = status.self_unlock ? "LSO Training was not completed within 45 days. Pass all modules and the final exam and your account unlocks automatically." : status.lock_reason || "Contact ALR corporate to restore access."; }
  if (s === "renewal_due") { title = `Certificate expired — renew within ${status.days_remaining ?? 0} day(s)`; body = "Your 365-day LSO certificate has expired. A new 30-day renewal window is now open."; }
  if (s === "renewal_overdue") { title = "Renewal window elapsed — admin reset required"; body = "Contact ALR corporate to restart your certification."; }
  if (s === "active" && (status.days_remaining ?? 999) <= 15 && !status.has_certificate) { title = `LSO Training due in ${status.days_remaining} day(s)`; body = "Complete your modules and final exam to stay in good standing."; }
  if (s === "certified" && (status.days_until_cert_expiry ?? 999) <= 30) { title = `Certificate expires in ${status.days_until_cert_expiry} day(s)`; body = "Plan to renew before expiry to avoid a 30-day renewal countdown."; }
  if (!title) return null;
  const color = lock ? "#ff6b6b" : s === "past_due" || s === "renewal_due" ? "#f5a623" : "var(--accent)";
  const Icon = lock ? Lock : s === "active" ? Clock : TriangleAlert;
  return (
    <div className="flex gap-3 rounded-2xl border p-4" style={{ borderColor: color, background: lock ? "#2a0a0a" : "#1A1A1A" }} data-testid="lso-status-banner">
      <Icon size={20} style={{ color }} className="mt-0.5 shrink-0" />
      <div><p className="text-sm font-bold">{title}</p><p className="mt-1 text-xs text-neutral-400">{body}</p></div>
    </div>
  );
};

const Waiver = ({ course, track, onDone }) => {
  const acks = course.waiver_acknowledgments || [];
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const all = acks.length > 0 && acks.every((a) => checked[a.key]);
  const submit = async () => {
    setBusy(true); setErr(null);
    try { await track.acceptWaiver(Object.fromEntries(acks.map((a) => [a.key, true]))); onDone(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="lso-waiver">
      <p className="font-display text-2xl font-bold uppercase">Safety acknowledgment</p>
      <p className="mt-1 text-sm text-neutral-400">Read and confirm each statement before starting the course.</p>
      <div className="mt-4 space-y-2">
        {acks.map((a) => (
          <label key={a.key} className="flex cursor-pointer items-start gap-3 rounded-lg border border-[#333] p-3 text-sm" data-testid={`waiver-${a.key}`}>
            <input type="checkbox" className="mt-1 h-4 w-4 accent-[var(--accent)]" checked={!!checked[a.key]} onChange={(e) => setChecked({ ...checked, [a.key]: e.target.checked })} />
            <span>{a.text}</span>
          </label>
        ))}
      </div>
      <ErrorNote>{err}</ErrorNote>
      <Btn className="mt-4" disabled={!all} busy={busy} onClick={submit} data-testid="waiver-submit">I ACKNOWLEDGE — START COURSE</Btn>
    </Card>
  );
};

const EmployeesList = ({ employees, total }) => {
  const navigate = useNavigate();
  return (
    <section className="pt-4" data-testid="lso-my-employees">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-[11px] font-bold tracking-[0.3em] text-neutral-400">MY EMPLOYEES</h2>
        <TextLink to="/lso/employees" data-testid="lso-manage-employees-link">Manage ›</TextLink>
      </div>
      {employees.length === 0 ? (
        <Card className="cursor-pointer text-center" onClick={() => navigate("/lso/employees")} data-testid="lso-add-first-employee">
          <UserPlus className="mx-auto text-[var(--accent)]" />
          <p className="mt-2 font-bold">Add Your First Employee</p>
          <p className="text-sm text-neutral-400">Each employee gets their own enrollment, progress tracking, and certificate.</p>
        </Card>
      ) : employees.map((emp) => (
        <button key={emp.id} onClick={() => navigate(`/lso?as=${emp.id}`)} data-testid={`lso-employee-${emp.id}`}
          className="mb-2 flex w-full items-center gap-3 rounded-2xl border border-[#333] bg-[#1A1A1A] p-4 text-left hover:border-[var(--accent)]">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#242424] font-bold text-[var(--accent)]">{(emp.name || "?")[0].toUpperCase()}</span>
          <span className="flex-1"><span className="block font-semibold">{emp.name}</span>
            <span className="text-xs text-neutral-400">{emp.role || "Operator"} • {emp.modules_passed || 0}/{total} modules{emp.has_certificate ? " • ✓ Course Complete" : ""}</span></span>
          <ChevronRight size={18} className="text-neutral-500" />
        </button>
      ))}
    </section>
  );
};

export default function LsoHub({ kind = "staff" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const as = kind === "rental" ? "me" : params.get("as") || "me";
  const track = lsoTrack(kind, as);
  const canManage = kind === "staff" && as === "me" && ["franchisee", "admin"].includes(user.role);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [course, enrollment, status, employees] = await Promise.all([
        track.getCourse(), track.start(), track.getStatus(),
        canManage ? api("/lso/employees").catch(() => []) : Promise.resolve([]),
      ]);
      setData({ course, enrollment, status, employees });
    } catch (e) { setErr(e.message || "Couldn't load LSO course"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, as, canManage]);
  useEffect(() => { load(); }, [load]);

  const back = kind === "rental" ? "/lso-rental" : "/dashboard";
  if (err) return <PortalPage title="Laser Safety Training" back={back} testId="lso-hub"><ErrorNote>{err}</ErrorNote>{kind === "rental" && <TextLink to="/lso-rental">Go to course access ›</TextLink>}</PortalPage>;
  if (!data) return <PortalPage title="Laser Safety Training" back={back} testId="lso-hub"><Spinner /></PortalPage>;

  const { course, enrollment, status, employees } = data;
  const total = course.modules.length;
  const nextN = nextModule(enrollment, total);
  const pct = total ? Math.round((passedCount(enrollment) / total) * 100) : 0;
  const hasCert = !!enrollment.certificate;
  const canFinal = canTakeFinal(enrollment, total);
  const learnerName = as === "me" ? user.full_name || "Yourself" : enrollment.learner_name || employees.find((e) => e.id === as)?.name || "Employee";
  const hardLock = as === "me" && status?.locked && !status?.self_unlock;
  const modulePath = (n) => `${track.hubPath}/module/${n}${qs(as)}`;

  if (kind === "rental" && !enrollment.waiver_accepted_at) {
    return <PortalPage eyebrow="ANSI Z136.1 ALIGNED" title={course.title || "LSO Operator Safety Training"} back={back} testId="lso-hub"><Waiver course={course} track={track} onDone={load} /></PortalPage>;
  }

  return (
    <PortalPage eyebrow="ANSI Z136.1 ALIGNED" title="Laser Safety Training" back={back} testId="lso-hub"
      actions={canManage && <button onClick={() => navigate("/lso/employees")} data-testid="lso-employees-btn" className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#333] bg-[#1A1A1A]"><Users size={20} /></button>}>
      {as === "me" && <StatusBanner status={status} />}
      <Card style={{ borderColor: "var(--accent)" }} data-testid="lso-learner-card">
        <p className="text-[10px] font-bold tracking-[0.2em] text-[var(--accent)]">{as === "me" ? "TAKING TEST AS YOURSELF" : "TAKING TEST AS EMPLOYEE"}</p>
        <p className="mt-1 text-lg font-bold" data-testid="lso-learner-name">{learnerName}</p>
        {enrollment.location && <p className="text-xs text-neutral-400">{enrollment.location}</p>}
        {as !== "me" && <Btn variant="outline" className="mt-3" onClick={() => navigate("/lso")} data-testid="lso-switch-to-me">Switch to Myself</Btn>}
      </Card>
      <Card>
        <p className="font-bold">{course.title}</p>
        {course.subtitle && <p className="mt-1 text-sm text-neutral-400">{course.subtitle}</p>}
        <p className="mt-3 flex items-center gap-2 text-xs text-neutral-400"><ShieldCheck size={14} className="text-[var(--accent)]" />{course.standard}</p>
        <p className="mt-1 flex items-center gap-2 text-xs text-neutral-400"><Award size={14} className="text-[var(--accent)]" />Pass: {course.pass_threshold_percent}% • Valid {course.validity_days || track.certValidity} days</p>
      </Card>
      <Card data-testid="lso-progress-card">
        <div className="flex items-center justify-between"><p className="font-bold">Your Progress</p><p className="text-lg font-bold text-[var(--accent)]" data-testid="lso-progress-pct">{pct}%</p></div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#242424]"><div className="h-full bg-[var(--accent)] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
        <p className="mt-2 text-xs text-neutral-400">{hasCert ? "Course complete — certificate issued." : `${passedCount(enrollment)} of ${total} modules passed`}</p>
        {hasCert
          ? <Btn className="mt-4" onClick={() => navigate(`${track.hubPath}/certificate${qs(as)}`)} data-testid="lso-view-certificate"><Award size={16} /> View Certificate</Btn>
          : hardLock
            ? <Btn variant="outline" className="mt-4" disabled data-testid="lso-hard-locked"><Lock size={16} /> Locked — contact ALR to restore access</Btn>
          : canFinal
            ? <Btn className="mt-4" disabled={hardLock} onClick={() => navigate(`${track.hubPath}/final-exam${qs(as)}`)} data-testid="lso-resume-final"><Trophy size={16} /> Take Final Exam</Btn>
            : <Btn className="mt-4" disabled={hardLock} onClick={() => navigate(modulePath(nextN))} data-testid="lso-resume"><Play size={16} /> {pct === 0 ? "Start Module 1" : `Resume Module ${nextN}`}</Btn>}
      </Card>
      <h2 className="pt-2 text-[11px] font-bold tracking-[0.3em] text-neutral-400">MODULES</h2>
      <div className="space-y-2" data-testid="lso-module-list">
        {course.modules.map((m) => {
          const mp = enrollment.modules_progress.find((p) => p.module_number === m.number);
          const passed = mp?.quiz_passed;
          const current = m.number === nextN && !hasCert;
          const locked = hardLock || (!hasCert && !passed && m.number > nextN);
          return (
            <button key={m.number} disabled={locked} onClick={() => navigate(modulePath(m.number))} data-testid={`lso-module-${m.number}`}
              className={`flex w-full items-center gap-4 rounded-2xl border bg-[#1A1A1A] p-4 text-left transition-colors disabled:opacity-50 ${current ? "border-[var(--accent)]" : "border-[#333] hover:border-[#555]"}`}>
              <ModuleBadge passed={passed} current={current} locked={locked} number={m.number} />
              <span className="flex-1">
                <span className="block text-sm font-semibold">{m.title}</span>
                {m.summary && <span className="line-clamp-2 block text-xs text-neutral-400">{m.summary}</span>}
                {mp?.quiz_score != null && <span className={`mt-1 block text-[11px] font-semibold ${passed ? "text-[var(--accent)]" : "text-[#FFB800]"}`}>Score: {Math.round(mp.quiz_score)}% • {mp.quiz_attempts} attempt{mp.quiz_attempts === 1 ? "" : "s"}</span>}
              </span>
              {!locked && <ChevronRight size={18} className="text-neutral-500" />}
            </button>
          );
        })}
      </div>
      <Card data-testid="lso-final-card">
        <p className="font-bold">Final Examination</p>
        <p className="mt-1 text-sm text-neutral-400">{course.final_exam.length} questions covering all modules. Pass at {course.pass_threshold_percent}% to earn your certificate.</p>
        {enrollment.final_exam && <p className="mt-2 text-xs font-semibold text-neutral-300">Best score: {Math.round(enrollment.final_exam.best_score)}% • {enrollment.final_exam.attempts} attempt{enrollment.final_exam.attempts === 1 ? "" : "s"}</p>}
        <Btn className="mt-4" disabled={!canFinal} onClick={() => navigate(`${track.hubPath}/final-exam${qs(as)}`)} data-testid="lso-take-final">
          <Trophy size={16} /> {!canFinal ? `Pass modules 1–${Math.min(10, total)} first` : enrollment.final_exam?.passed ? "Retake Final Exam" : "Take Final Exam"}
        </Btn>
      </Card>
      {canManage && <EmployeesList employees={employees} total={total} />}
    </PortalPage>
  );
}
